import { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Mail,
  Search,
  Users,
  Building2,
  ShieldCheck,
  RefreshCw,
  Sparkles,
  ListPlus,
  Send,
  Trash2,
  SlidersHorizontal,
  CircleStop,
} from "lucide-react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { StatsCard } from "@crm-src/components/stats-card";
import { useToast } from "@crm-src/hooks/use-toast";
import usePermissions from "@crm-src/hooks/usePermissions";
import { fetchAiAudience, importAiContacts, type AiAudienceSource } from "@crm-src/lib/ai-audience-api";
import {
  listColdEmails,
  buildColdEmailQueue,
  generatePendingColdEmails,
  generateColdEmail,
  approveColdEmail,
  deleteColdEmail,
  stopColdEmailGeneration,
  updateColdEmailSettings,
  coldEmailAudiencePreview,
  type ColdEmailBatch,
  type ColdEmailListResponse,
  type ColdEmailStats,
} from "@crm-src/lib/cold-email-api";
import { coldStatusChip } from "@crm-src/lib/cold-email-format";
import { ColdEmailModal } from "./ColdEmailModal";

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "pending_review", label: "Needs review" },
  { value: "pending_generation", label: "Awaiting draft" },
  { value: "failed", label: "Failed" },
  { value: "sent", label: "Sent" },
  { value: "skipped", label: "Skipped" },
];

function preview(text: string | null, max = 70): string {
  if (!text) return "";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

export function ColdEmailQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { isSuperAdmin } = usePermissions();
  const canSend = isSuperAdmin();

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [batches, setBatches] = useState<ColdEmailBatch[]>([]);
  const [stats, setStats] = useState<ColdEmailStats | null>(null);
  const [audience, setAudience] = useState<{ contacts: number; companies: number } | null>(null);
  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({ perPage: 25, totalCount: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<"" | "build" | "generate" | "sync" | "stop" | "regen">("");
  const [confirmRegen, setConfirmRegen] = useState(false);
  // Per-row inline generate/send in flight (keyed by batch id).
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  // The company whose email is open in the modal.
  const [modalId, setModalId] = useState<number | null>(null);
  // Quick "Send" from a list row → confirm.
  const [sendTarget, setSendTarget] = useState<ColdEmailBatch | null>(null);
  // Quick "Delete" from a list row → confirm.
  const [deleteTarget, setDeleteTarget] = useState<ColdEmailBatch | null>(null);
  // How many companies to draft per background run (operator-controlled).
  const [draftCount, setDraftCount] = useState(25);
  // Generator + sender settings (current values, shown in the modal From row).
  const [steering, setSteering] = useState("");
  const [fromName, setFromName] = useState("XOP Networks");
  const [fromAddress, setFromAddress] = useState("sgupta@xopnetworks.com");
  // Draft copies edited inside the Generator dialog.
  const [steeringDraft, setSteeringDraft] = useState("");
  const [fromNameDraft, setFromNameDraft] = useState("");
  const [fromAddressDraft, setFromAddressDraft] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSteering, setSavingSteering] = useState(false);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setLoading(true);
      try {
        const res: ColdEmailListResponse = await listColdEmails({
          page,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(search ? { search } : {}),
        });
        setBatches(res.batches);
        setStats(res.stats);
        if (res.settings) {
          setSteering(res.settings.steering ?? "");
          setFromName(res.settings.from_name || "XOP Networks");
          setFromAddress(res.settings.from_address || "sgupta@xopnetworks.com");
        }
        setPageMeta({
          perPage: res.pagination.per_page || 25,
          totalCount: res.pagination.total_count || 0,
          totalPages: res.pagination.total_pages || 1,
        });
        setForbidden(false);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          setForbidden(true);
        } else if (!opts.silent) {
          toast({ title: "Could not load the cold email queue", variant: "destructive" });
        }
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [page, statusFilter, search, toast],
  );

  const loadAudience = useCallback(async () => {
    try {
      setAudience(await coldEmailAudiencePreview());
    } catch {
      /* non-fatal — the count is a convenience */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadAudience();
  }, [loadAudience]);

  // While any batch is still being drafted in the background, poll so the queue
  // reflects generation progress without the operator refreshing.
  useEffect(() => {
    const stillWorking = batches.some(
      (b) => b.status === "generating" || b.status === "pending_generation",
    );
    if (!stillWorking) return;
    pollRef.current = setTimeout(() => void load({ silent: true }), 4000);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [batches, load]);

  const handleBuild = async (): Promise<void> => {
    setBusy("build");
    try {
      const res = await buildColdEmailQueue();
      toast({
        title: "Queue built",
        description:
          res.companies_created > 0
            ? `${res.companies_created} companies queued (${res.recipients_created} contacts). ${res.companies_skipped} already queued.`
            : `No new companies to queue. ${res.companies_skipped} already in the queue.`,
      });
      setPage(1);
      await Promise.all([load(), loadAudience()]);
    } catch {
      toast({ title: "Could not build the queue", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleGeneratePending = async (): Promise<void> => {
    setBusy("generate");
    try {
      const res = await generatePendingColdEmails({ limit: draftCount });
      toast({
        title: res.queued > 0 ? "Drafting started" : "Nothing to draft",
        description:
          res.queued > 0
            ? `Drafting ${res.queued} companies in the background. Use Stop to halt.`
            : "Every queued company already has a draft.",
      });
      await load({ silent: true });
    } catch {
      toast({ title: "Could not start drafting", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  // Re-draft every already-drafted company (pending_review + failed) with the
  // current settings/prompt/sender. Background + stoppable. Overwrites edits.
  const handleRegenerateAll = async (): Promise<void> => {
    setBusy("regen");
    try {
      const res = await generatePendingColdEmails({ scope: "drafted", limit: 1000 });
      toast({
        title: res.queued > 0 ? "Regenerating" : "Nothing to regenerate",
        description:
          res.queued > 0
            ? `Re-drafting ${res.queued} emails in the background with your current settings. Use Stop to halt.`
            : "No drafted emails to regenerate yet.",
      });
      await load({ silent: true });
    } catch {
      toast({ title: "Could not regenerate", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleStop = async (): Promise<void> => {
    setBusy("stop");
    try {
      await stopColdEmailGeneration();
      toast({ title: "Drafting stopped", description: "Queued drafts were halted. Nothing was sent." });
      await load({ silent: true });
    } catch {
      toast({ title: "Could not stop drafting", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleSaveSteering = async (): Promise<void> => {
    setSavingSteering(true);
    try {
      const updated = await updateColdEmailSettings({
        steering: steeringDraft,
        from_name: fromNameDraft.trim(),
        from_address: fromAddressDraft.trim(),
      });
      setSteering(updated.steering ?? "");
      setFromName(updated.from_name);
      setFromAddress(updated.from_address);
      setSettingsOpen(false);
      toast({ title: "Generator updated", description: "New drafts + the sender are set." });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({ title: "Could not save", description: msg || undefined, variant: "destructive" });
    } finally {
      setSavingSteering(false);
    }
  };

  const handleDeleteRow = async (): Promise<void> => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setRowBusy(id);
    try {
      await deleteColdEmail(id);
      await load({ silent: true });
      toast({ title: "Deleted", description: `Removed ${deleteTarget.company_name}.` });
    } catch {
      toast({ title: "Could not delete", variant: "destructive" });
    } finally {
      setRowBusy(null);
    }
  };

  // Pull the harvester + sales-lead pools into the CRM directory so the cold
  // audience includes them (reuses the Email Campaigns audience bridge).
  const handleSyncAiAudience = async (): Promise<void> => {
    setBusy("sync");
    try {
      const sources: AiAudienceSource[] = ["harvester", "sales_leads"];
      let imported = 0;
      for (const source of sources) {
        const fetched = await fetchAiAudience(source);
        if (fetched.contacts.length > 0) {
          const outcome = await importAiContacts(source, fetched.contacts);
          imported += outcome.created + outcome.matched;
        }
      }
      toast({
        title: "AI audience synced",
        description: `${imported} harvester / sales-lead contacts are in the directory. Build the queue to include them.`,
      });
      await loadAudience();
    } catch {
      toast({
        title: "Could not sync the AI audience",
        description: "The sales agent may be unreachable. The rest of the queue still works.",
        variant: "destructive",
      });
    } finally {
      setBusy("");
    }
  };

  // Inline "Generate" on an Awaiting-draft row — drafts that one company (synchronous)
  // and opens the modal to show the result.
  const handleRowGenerate = async (b: ColdEmailBatch): Promise<void> => {
    setRowBusy(b.id);
    try {
      await generateColdEmail(b.id);
      await load({ silent: true });
      setModalId(b.id);
    } catch {
      toast({ title: "Generation failed", description: "The AI service may be busy. Try again.", variant: "destructive" });
    } finally {
      setRowBusy(null);
    }
  };

  // Confirmed quick-send from a list row (super admin).
  const handleRowSend = async (): Promise<void> => {
    if (!sendTarget) return;
    const id = sendTarget.id;
    setRowBusy(id);
    try {
      await approveColdEmail(id);
      await load({ silent: true });
      toast({ title: "Sending", description: `Delivering to all contacts at ${sendTarget.company_name}.` });
    } catch {
      toast({ title: "Could not send", variant: "destructive" });
    } finally {
      setRowBusy(null);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Cold Email Agent" description="AI-drafted, CEO-approved cold outreach" />
        <Alert severity="warning" sx={{ mt: 3 }}>
          The Cold Email Agent is a company-wide outreach tool and needs access to all CRM records.
          Your role is scoped to your own records, so it is not available. Ask an administrator for
          all-records access if you need it.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Cold Email Agent"
        description="AI drafts a warm, researched email for every company that is not a customer or a prospect. You review and approve each one before it sends."
        actions={
          <>
            <Tooltip title="Tell the AI how to write (tone, length, focus)">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SlidersHorizontal size={16} />}
                  onClick={() => {
                    setSteeringDraft(steering);
                    setFromNameDraft(fromName);
                    setFromAddressDraft(fromAddress);
                    setSettingsOpen(true);
                  }}
                >
                  Generator
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Pull harvester + sales-lead contacts into the directory">
              <span>
                <Button
                  variant="outlined"
                  startIcon={busy === "sync" ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                  disabled={busy !== ""}
                  onClick={() => void handleSyncAiAudience()}
                >
                  Sync AI audience
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={busy === "build" ? <CircularProgress size={16} color="inherit" /> : <ListPlus size={16} />}
              disabled={busy !== ""}
              onClick={() => void handleBuild()}
            >
              Build queue
            </Button>
          </>
        }
      />

      <Alert severity="info" icon={<ShieldCheck size={20} />}>
        Automatic sending is <strong>off</strong>. Every email is drafted for review and only goes
        out after the CEO approves it. Customers and existing prospects are never contacted.
      </Alert>

      {stats && (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          }}
        >
          <StatsCard title="Needs review" value={stats.pending_review} icon={Mail} />
          <StatsCard title="Awaiting draft" value={stats.pending_generation} icon={Sparkles} />
          <StatsCard title="Sent" value={stats.sent} icon={ShieldCheck} />
          <StatsCard title="Companies queued" value={stats.total} icon={Building2} />
        </Box>
      )}

      <Card variant="outlined">
        <CardContent sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Users size={20} />
          <Typography sx={{ color: "text.secondary" }}>
            {audience
              ? `${audience.contacts.toLocaleString()} cold contacts across ${audience.companies.toLocaleString()} companies are ready to queue.`
              : "Counting cold contacts…"}
          </Typography>
          {stats && (stats.pending_generation > 0 || stats.generating > 0 || stats.pending_review > 0) && (
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              {stats.generating > 0 && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<CircularProgress size={12} />}
                  label={`Drafting ${stats.generating}…`}
                />
              )}
              {stats.pending_generation > 0 && (
                <>
                  <TextField
                    type="number"
                    size="small"
                    label="How many"
                    value={draftCount}
                    onChange={(e) => setDraftCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                    sx={{ width: 110 }}
                    inputProps={{ min: 1, max: 500 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={busy === "generate" ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                    disabled={busy !== ""}
                    onClick={() => void handleGeneratePending()}
                  >
                    Draft {Math.min(draftCount, stats.pending_generation)}
                  </Button>
                </>
              )}
              {stats.pending_review > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={busy === "regen" ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
                  disabled={busy !== ""}
                  onClick={() => setConfirmRegen(true)}
                >
                  Regenerate all
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={busy === "stop" ? <CircularProgress size={14} /> : <CircleStop size={14} />}
                disabled={busy !== ""}
                onClick={() => void handleStop()}
              >
                Stop
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 180 }}
        >
          {STATUS_FILTERS.map((f) => (
            <MenuItem key={f.value || "all"} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="Search company or subject"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : batches.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Mail size={32} style={{ opacity: 0.4 }} />
            <Typography sx={{ mt: 2, fontWeight: 600 }}>The queue is empty</Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
              Click <strong>Build queue</strong> to group your cold contacts by company, then draft
              and review each email.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TableContainer component={Card} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell align="right">Contacts</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((b) => {
                  const chip = coldStatusChip(b.status);
                  const rowLoading = rowBusy === b.id;
                  const needsDraft = b.status === "pending_generation" || b.status === "failed";
                  const reviewable = b.status === "pending_review";
                  return (
                    <TableRow
                      key={b.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setModalId(b.id)}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{b.company_name}</Typography>
                        {b.company_domain && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {b.company_domain}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{b.industry || "—"}</TableCell>
                      <TableCell align="right">{b.recipients_count}</TableCell>
                      <TableCell>
                        <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", maxWidth: 320 }}>
                        {b.subject ? preview(b.subject) : <em>Not drafted yet</em>}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                          {needsDraft && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={rowLoading ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                              disabled={rowLoading}
                              onClick={() => void handleRowGenerate(b)}
                            >
                              Generate
                            </Button>
                          )}
                          <Button size="small" variant="text" onClick={() => setModalId(b.id)}>
                            View
                          </Button>
                          {reviewable && canSend && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={rowLoading ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
                              disabled={rowLoading}
                              onClick={() => setSendTarget(b)}
                            >
                              Send
                            </Button>
                          )}
                          {b.status !== "sending" && b.status !== "sent" && (
                            <Tooltip title="Delete this draft">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Delete ${b.company_name}`}
                                  disabled={rowLoading}
                                  onClick={() => setDeleteTarget(b)}
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            page={page}
            perPage={pageMeta.perPage}
            totalCount={pageMeta.totalCount}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            entityNoun="companies"
            hideIfSinglePage
          />
        </>
      )}

      <ColdEmailModal
        batchId={modalId}
        open={modalId !== null}
        fromName={fromName}
        fromAddress={fromAddress}
        onClose={() => setModalId(null)}
        onChanged={() => void load({ silent: true })}
      />

      {sendTarget && (
        <ConfirmDialog
          isOpen={sendTarget !== null}
          onClose={() => setSendTarget(null)}
          title={`Send to all contacts at ${sendTarget.company_name}?`}
          description={`This delivers the email to all ${sendTarget.recipients_count} contact(s). This cannot be undone.`}
          variant="warning"
          confirmLabel="Approve & send"
          onConfirm={async () => {
            await handleRowSend();
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          title={`Delete the draft for ${deleteTarget.company_name}?`}
          description="This removes the queued email and its recipients. You can rebuild the queue later if needed."
          variant="destructive"
          confirmLabel="Delete"
          onConfirm={async () => {
            await handleDeleteRow();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmRegen}
        onClose={() => setConfirmRegen(false)}
        title={`Regenerate all ${stats?.pending_review ?? 0} drafted emails?`}
        description="This re-drafts every reviewed email with fresh AI copy using your current sender, signature, and instructions. Any edits you saved to those drafts will be replaced. Nothing is sent."
        variant="warning"
        confirmLabel="Regenerate all"
        onConfirm={async () => {
          await handleRegenerateAll();
        }}
      />

      {/* Generator controls — the sender + how the AI writes every draft. */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generator &amp; sender</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Sender</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            Who cold emails come from. This applies to every cold email. The mailbox must be an
            @xopnetworks.com address; <strong>marcom@xopnetworks.com</strong> is the verified sender.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <TextField
              label="From name"
              value={fromNameDraft}
              onChange={(e) => setFromNameDraft(e.target.value.slice(0, 100))}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <TextField
              label="From address"
              value={fromAddressDraft}
              onChange={(e) => setFromAddressDraft(e.target.value.slice(0, 200))}
              placeholder="sgupta@xopnetworks.com"
              sx={{ flex: 1, minWidth: 220 }}
              error={fromAddressDraft.trim() !== "" && !/@xopnetworks\.com$/i.test(fromAddressDraft.trim())}
              helperText={
                fromAddressDraft.trim() !== "" && !/@xopnetworks\.com$/i.test(fromAddressDraft.trim())
                  ? "Must end in @xopnetworks.com"
                  : "e.g. sgupta@ or marcom@xopnetworks.com"
              }
            />
          </Box>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>AI writing instructions</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            Tell the AI how to write every cold email. These override the built-in style, so keep them
            short and specific. Examples: &ldquo;Under 60 words, punchy, no corporate fluff&rdquo; or
            &ldquo;Lead with a question, one clear benefit, casual tone.&rdquo;
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Writing instructions"
            placeholder="e.g. Under 60 words. Plain, direct. One benefit. No hype."
            value={steeringDraft}
            onChange={(e) => setSteeringDraft(e.target.value.slice(0, 2000))}
            helperText={`${steeringDraft.length}/2000 · applies to new drafts (Generate / Regenerate)`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" color="inherit" onClick={() => setSettingsOpen(false)} disabled={savingSteering}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={savingSteering ? <CircularProgress size={16} color="inherit" /> : undefined}
            disabled={
              savingSteering ||
              (fromAddressDraft.trim() !== "" && !/@xopnetworks\.com$/i.test(fromAddressDraft.trim()))
            }
            onClick={() => void handleSaveSteering()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ColdEmailQueuePage;
